import {
  PageHeader,
  Table,
  Tooltip,
  notification,
  Row,
  Col,
  Button,
} from "antd";

import { useCallback, useEffect, useState, useRef } from "react";
import IconTutorials from "../../static/icon/IconTutorials";
import IconNotFound from "../../static/icon/IconNotFound";

import {
  ArrowRightOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { Label } from "reactstrap";
import update from "immutability-helper";
import { HTML5Backend } from "react-dnd-html5-backend";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { Prompt } from "react-router-dom";
import { useAuth } from "../../context/auth";

const type = "DraggableBodyRow";

const DraggableBodyRow = ({
  index,
  moveRow,
  className,
  style,
  ...restProps
}) => {
  const ref = useRef();
  const [{ isOver, dropClassName }, drop] = useDrop({
    accept: type,
    collect: (monitor) => {
      const { index: dragIndex } = monitor.getItem() || {};
      if (dragIndex === index) return {};
      return {
        isOver: monitor.isOver(),
        dropClassName: dragIndex < index ? " drop-over-downward" : " drop-over-upward",
      };
    },
    drop: (item) => {
      moveRow(item.index, index);
    },
  });
  const [, drag] = useDrag({
    type,
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });
  drop(drag(ref));

  return (
    <tr
      ref={ref}
      className={`${className}${isOver ? dropClassName : ""}`}
      style={{ cursor: "move", ...style }}
      {...restProps}
    />
  );
};

const GuideManage = () => {
  const { accessToken } = useAuth();

  const components = { body: { row: DraggableBodyRow } };

  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [pageValue, setPageValue] = useState({});

  const [dataSource, setDataSource] = useState([]);
  const [dataSourceDisplay, setDataSourceDisplay] = useState([]);

  const [changed, setChange] = useState(false);

  const columns = [
    {
      dataIndex: "titleEn",
      key: "titleEn",
      render: (text) => {
        return (
          <Tooltip title={text}>
            <div className="ellipsis-two w-76">{text}</div>
          </Tooltip>
        );
      },
    },
    {
      key: "action",
      align: "center",
      width: 80,
      render: (_, page) => {
        return (
          <Tooltip placement="bottom" title="Add New">
            <ArrowRightOutlined
              style={{ color: "#3E91F7" }}
              onClick={() => {
                setDataSource(dataSource => dataSource.filter((item) => item.id !== page.id));
                setDataSourceDisplay(dataSourceDisplay => [
                  ...dataSourceDisplay,
                  {
                    hierarchy: dataSourceDisplay.length + 1,
                    id: page.id,
                    titleEn: page.titleEn,
                    titleTh: page.titleTh,
                  },
                ]);
                setChange(true);
              }}
            />
          </Tooltip>
        );
      },
    },
  ];

  const columnsdisplay = [
    {
      align: "center",
      width: 70,
      render: () => {
        return (
          <Tooltip>
            <div className="ellipsis-two  m-0">
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M0.972655 6.94508H15.0276C15.5639 6.94508 16 6.50896 16 5.97266C16 5.43635 15.5639 5 15.0276 5H0.972655C0.43635 5 0 5.43635 0 5.97266C0 6.50896 0.43635 6.94508 0.972655 6.94508Z"
                  fill="#4F4F4F"
                />
                <path
                  d="M15.0276 10.0195H0.972655C0.43635 10.0195 0 10.4559 0 10.9922C0 11.5285 0.43635 11.9646 0.972655 11.9646H15.0276C15.5639 11.9646 16 11.5285 16 10.9922C16 10.4559 15.5639 10.0195 15.0276 10.0195Z"
                  fill="#4F4F4F"
                />
              </svg>
            </div>
          </Tooltip>
        );
      },
    },
    {
      dataIndex: "titleEn",
      key: "titleEn",
      render: (text) => {
        return (
          <Tooltip title={text}>
            <div className="ellipsis-two w-76">{text}</div>
          </Tooltip>
        );
      },
    },
    {
      key: "action",
      align: "center",
      width: 80,
      hidden: true,
      render: (_, page) => {
        return (
          <Tooltip placement="bottom" title="Delete">
            <CloseOutlined
              style={{ color: "#BDBDBD" }}
              onClick={() => {
                setDataSource(dataSource => [
                  ...dataSource,
                  {
                    id: page.id,
                    titleEn: page.titleEn,
                    titleTh: page.titleTh,
                  },
                ]);
                setDataSourceDisplay(dataSourceDisplay => dataSourceDisplay.filter(item => item.id !== page.id));
                setChange(true);
              }}
            />
          </Tooltip>
        );
      },
    },
  ];

  useEffect(() => {
    async function getMTDisplay() {
      setLoading(true);

      const url = new URL('/api/page', window.location);
      url.searchParams.append('type', 'guide');
      url.searchParams.append('minWeight', '0');

      url.searchParams.append('page', '1');
      url.searchParams.append('size', '99');
      url.searchParams.append('sort', 'weight');
      url.searchParams.append('order', 'asc');

      const res = await fetch(url, {
        headers: { 'X-ACCESS-TOKEN': accessToken },
      });
      if (res.ok) {
        const { pages } = await res.json();
        setDataSourceDisplay(pages);
      } else {
        notification["error"]({
          message: "Something went wrong !",
          description: `Please re-login or contact web admin. (Code: ${await res.json().then(data => data.message)})`,
        });
      }

      setLoading(false);
    };

    getMTDisplay();
  }, [accessToken]);

  const hierarchyNew = dataSourceDisplay.map((item, i) => {
    const container = {};

    container.hierarchy = i + 1;
    container.id = item.id;
    container.titleEn = item.titleEn;
    container.titleTh = item.titleTh;

    return container;
  });

  const save = async () => {
    for (const page of [...dataSource, ...hierarchyNew]) {
      const url = new URL('/api/page/' + page.id, window.location);

      const res = await fetch(url, {
        headers: {
          'X-ACCESS-TOKEN': accessToken,
          'Content-type': 'application/json',
        },
        method: 'PATCH',
        body: JSON.stringify({ weight: page.hierarchy ?? 0 }),
      });
      if (!res.ok) {
        notification["error"]({
          message: "Something went wrong !",
          description: `Please re-login or contact web admin. (Code: ${await res.json().then(data => data.message)})`,
        });
      }
    }

    notification["success"]({
      message: "Done!",
    });

    setChange(false);
  };

  useEffect(() => {
    async function getCTList() {
      setLoading(true);

      const url = new URL('/api/page', window.location);
      url.searchParams.append('type', 'guide');
      url.searchParams.append('weight', '0');

      if (pageValue.page) url.searchParams.append('page', pageValue.page);
      if (pageValue.pageSize) url.searchParams.append('size', pageValue.pageSize);

      const res = await fetch(url, {
        headers: { 'X-ACCESS-TOKEN': accessToken },
      });
      if (res.ok) {
        const { total, pages } = await res.json();
        setTotal(total);
        setDataSource(pages);
      } else {
        notification["error"]({
          message: "Something went wrong !",
          description: `Please re-login or contact web admin. (Code: ${await res.json().then(data => data.message)})`,
        });
      }

      setLoading(false);
    };

    getCTList();
  }, [accessToken, pageValue]);

  const moveRow = useCallback(
    (dragIndex, hoverIndex) => {
      const dragRow = dataSourceDisplay[dragIndex];
      setDataSourceDisplay(
        update(dataSourceDisplay, {
          $splice: [
            [dragIndex, 1],
            [hoverIndex, 0, dragRow],
          ],
        })
      );
    },
    [dataSourceDisplay]
  );

  return (
    <>
      <div className="flex justify-between items-center mt-2">
        <PageHeader
          style={{ padding: 0, width: "100%" }}
          title={
            <span className="flex items-center mt-2">
              <IconTutorials /> &nbsp;&nbsp;Manage Tutorials
            </span>
          }
        />
      </div>

      <div className="">
        <Row>
          <Col
            sm={24}
            md={24}
            lg={12}
            style={{ marginRight: "auto", paddingRight: 15 }}
          >
            <div className="content">
              <div className="flex justify-between items-center mt-2">
                <PageHeader
                  style={{ padding: 0, width: "60%" }}
                  title={
                    <span className="flex items-center mt-2">
                      <Label style={{ fontSize: 14, fontWeight: 500 }}>
                        &nbsp;&nbsp;Tutorial Title
                      </Label>
                    </span>
                  }
                />
              </div>

              <DndProvider backend={HTML5Backend}>
                <Table
                  locale={{ emptyText: <IconNotFound /> }}
                  className="inbox_table"
                  rowKey={(record) => record.id}
                  columns={columns}
                  loading={loading}
                  dataSource={dataSource}
                  showHeader={false}
                  pagination={{
                    size: "small",
                    current: pageValue.page,
                    total,
                    showQuickJumper: true,
                    showSizeChanger: true,
                    showTotal: (total, range) => ` ${range[0]}-${range[1]} of ${total} `,
                  }}
                  onChange={(pagination) => {
                    setPageValue({
                      page: pagination.current,
                      pageSize: pagination.pageSize,
                    });
                  }}
                />
              </DndProvider>
            </div>
          </Col>

          <Col
            sm={24}
            md={24}
            lg={12}
            style={{ marginRight: "auto", paddingRight: 15 }}
          >
            <div className="content">
              <Row align="middle" className="flex justify-content-between my-4">
                <Col
                  sm={24}
                  md={24}
                  lg={20}
                  style={{ marginRight: "auto" }}
                >
                  <Label>Display Tutorials ({dataSourceDisplay.length})</Label>
                </Col>
                <Col
                  sm={12}
                  md={12}
                  lg={4}
                  className="flex justify-between items-center"
                  style={{ marginRight: "auto" }}
                >
                  <Button
                    onClick={save}
                    className="px-6"
                    key="save"
                    type="primary"
                    shape="round"
                    loading={loading}
                  >
                    Save
                  </Button>
                </Col>
              </Row>

              <DndProvider backend={HTML5Backend}>
                <Table
                  locale={{ emptyText: <IconNotFound /> }}
                  showHeader={false}
                  columns={columnsdisplay}
                  loading={loading}
                  dataSource={dataSourceDisplay}
                  pagination={false}
                  components={components}
                  onRow={(record, index) => ({
                    index,
                    moveRow,
                  })}
                />
              </DndProvider>
            </div>
          </Col>
        </Row>
      </div>
      <Prompt when={changed} message="Changes will not be saved. Do you want to proceed ?" />
    </>
  );
};

export default GuideManage;
